import type {INetsMap} from "@buildwithflux/models";
import {Records} from "@buildwithflux/shared";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";
import {emptyArray} from "../utils";

/**
 * Base selectors for nets - direct state reads
 * These selectors perform simple property access without complex computation
 */

/** Gets all nets from the document */
export const all: DocumentSelector<INetsMap | undefined> = (state: DocumentStoreState) => state.nets;

/** Gets a net by UID */
export const byUid: ParameterizedSelector<string, INetsMap[string] | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.nets?.[uid];
};

/** Gets multiple nets by UIDs */
export const byUids: ParameterizedSelector<string[], Array<INetsMap[string]>> = (uids: string[]) => {
    return (state: DocumentStoreState) => {
        const nets = state.nets;
        if (!nets) return [];
        const result: Array<INetsMap[string]> = [];
        for (const uid of uids) {
            const net = nets[uid];
            if (net) {
                result.push(net);
            }
        }
        return result;
    };
};

/** Gets all net UIDs */
export const uids: DocumentSelector<string[]> = (state: DocumentStoreState) => {
    const nets = state.nets;
    if (!nets) return emptyArray;
    return Object.keys(nets);
};

/** Counts total nets */
export const count: DocumentSelector<number> = (state: DocumentStoreState) => {
    const nets = state.nets;
    if (!nets) return 0;
    return Records.count(nets);
};

/** Checks if document has any nets */
export const hasNets: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const nets = state.nets;
    if (!nets) return false;
    return !Records.isEmpty(nets);
};

/** Gets a net's label by UID */
export const labelByUid: ParameterizedSelector<string, string | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.nets?.[uid]?.label;
};

/** Gets a net's properties by UID */
export const propertiesByUid: ParameterizedSelector<string, INetsMap[string]["properties"] | undefined> = (
    uid: string,
) => {
    return (state: DocumentStoreState) => state.nets?.[uid]?.properties;
};

/** Gets the label of the first net found in a list of UIDs */
export const firstLabelByUids: ParameterizedSelector<string[], string> = (uids: string[]) => {
    return (state: DocumentStoreState) => {
        for (const uid of uids) {
            const net = state.nets?.[uid];
            if (net) {
                return net.label;
            }
        }
        return "";
    };
};

/** Counts how many of the given UIDs are nets */
export const selectedCount: ParameterizedSelector<string[] | undefined, number> = (uids) => {
    return (state: DocumentStoreState) => {
        if (!state.nets || !uids) {
            return 0;
        }
        let count = 0;
        for (const uid of uids) {
            if (state.nets[uid]) {
                count++;
            }
        }
        return count;
    };
};
